/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Observable, of } from "rxjs";
import { concatMap, map } from "rxjs/operators";
import { MyInjector } from "@src/app/app.module";
import { Txn } from "@src/app/model/txn";
import { ActionColor, IFormAction } from "@src/app/shared/form/form.component";
import { InvoiceService } from "@src/app/modules/txn/invoice.service";
import { FormTextAreaComponent } from "@src/app/shared/form/form-text-area/form-text-area.component";
import { EditFieldDialogComponent, EditFieldDialogOptions } from "@src/app/shared/dialogs/edit-field-dialog/edit-field-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { ConfirmDialogService } from "@src/app/shared/dialogs/confirmDialog";
import { RegeneratePDFAction } from "@src/app/modules/txn/invoice-page/regenerate-invoice-pdf";
import { MailInvoicePDFAction } from "@src/app/modules/txn/invoice-page/mail-invoice-pdf-action";

export class EditInvoiceNotesAction implements IFormAction {
    name = $localize`Edit Comments`;
    color: ActionColor = 'primary';
    show = false;
    icon = 'edit_note';
    approvalNeeded = false;
    disabled = false;
    approvalText: string;
    tipText: string;
    txn: Txn;

    invoiceSvc = MyInjector.instance.get(InvoiceService);

    action() {
        const dialog = MyInjector.instance.get(MatDialog);
        const notesField = FormTextAreaComponent.make('Comments', 'notes', {});
        const data = new EditFieldDialogOptions({
            field: notesField,
            service: this.invoiceSvc,
            id: this.txn.id,
            forceTeamId: this.txn.teamId,
        });

        return dialog.open(EditFieldDialogComponent, { data }).afterClosed().pipe(
            concatMap((updatedTxn: Txn | null) => {
                if (!updatedTxn) {
                    return of(null);
                }
                const regenerate = new RegeneratePDFAction();
                regenerate.setup(updatedTxn);
                return (regenerate.action() as Observable<Txn | null>).pipe(
                    concatMap(afterRegen => {
                        if (!afterRegen) {
                            const cds = MyInjector.instance.get(ConfirmDialogService);
                            return cds.alert(
                                $localize`PDF not regenerated`,
                                $localize`You have not regenerated the PDF version of this charge. The note you have added will not be visible to the member unless you do. You can click on the Regenerate PDF action if and when you are ready to make the member aware of this new note.`
                            ).afterClosed().pipe(map(() => updatedTxn));
                        }
                        const mail = new MailInvoicePDFAction();
                        mail.setup(afterRegen);
                        return (mail.action() as Observable<Txn | null>).pipe(
                            map(mailResult => mailResult ?? afterRegen)
                        );
                    })
                );
            })
        );
    }

    setup(txn: Txn) {
        this.show = true;
        this.txn = txn;
    }
}
